
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { saveAs } from "file-saver";

import Loader from "../../components/Loader";
import ProfileEditor from "./ProfileEditor";
import {
    createProfile,
    deleteProfile,
    duplicateProfile,
    exportProfileJson,
    getSignMeta,
    importSignJson,
    listAssets,
    listProfiles,
    listSigns,
    readApiError,
    readJsonFile,
} from "./printSignApi";
import { JsonImportButton, formatDateTime } from "./signUi";
import { ActionsMenu, PageMessage, SIGNS_ROOT, SignsPage, StatusBadge } from "./signPage";

/*
 * Профили бренда — данные фирмы для вывесок (потом и визиток).
 * Список, как у смет; профиль открывается своей страницей: слева поля, справа вывеска.
 */

const PROFILES_ROOT = `${SIGNS_ROOT}/profiles`;

export const PrintSignProfilesPage = () => {
    const navigate = useNavigate();
    const [profiles, setProfiles] = useState([]);
    const [signs, setSigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [busy, setBusy] = useState({});
    const [message, setMessage] = useState(null);
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [nextProfiles, nextSigns] = await Promise.all([listProfiles(), listSigns()]);
            setProfiles(nextProfiles);
            setSigns(nextSigns);
        } catch (error) {
            setMessage({ type: "error", text: await readApiError(error, "Не удалось загрузить профили") });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return profiles;
        return profiles.filter((item) => [item.name, item.brandName, ...(item.phones || [])].some((value) => String(value || "").toLowerCase().includes(needle)));
    }, [profiles, query]);

    const run = async (profile, label, task, errorText) => {
        setBusy((current) => ({ ...current, [profile._id]: label }));
        setMessage(null);
        try {
            await task();
        } catch (error) {
            setMessage({ type: "error", text: await readApiError(error, errorText) });
        } finally {
            setBusy((current) => {
                const next = { ...current };
                delete next[profile._id];
                return next;
            });
        }
    };

    const create = async () => {
        setCreating(true);
        try {
            const profile = await createProfile({ name: "Новый профиль" });
            navigate(`${PROFILES_ROOT}/${profile._id}`);
        } catch (error) {
            setMessage({ type: "error", text: await readApiError(error, "Не удалось создать профиль") });
            setCreating(false);
        }
    };

    const importJson = async (file) => {
        setCreating(true);
        setMessage(null);
        try {
            // Сначала пустой профиль, потом в него — данные из файла (вывеска не создаётся).
            const profile = await createProfile({ name: file.name.replace(/\.json$/i, "") || "Профиль" });
            await importSignJson({ data: await readJsonFile(file), profileId: profile._id });
            navigate(`${PROFILES_ROOT}/${profile._id}`);
        } catch (error) {
            setMessage({ type: "error", text: await readApiError(error, "Не удалось загрузить JSON") });
            setCreating(false);
            load();
        }
    };

    return (
        <SignsPage title="Профили бренда" subtitle="Данные фирмы для вывесок: название, логотип, контакты, режим работы, услуги" back={SIGNS_ROOT} backLabel="К вывескам">
            <div className="signsPage__toolbar">
                <label className="signsSearch">
                    <span aria-hidden="true">⌕</span>
                    <input type="text" value={query} placeholder="Поиск по названию, фирме, телефону…" onChange={(event) => setQuery(event.target.value)} />
                </label>
                <JsonImportButton className="signsButton" disabled={creating} onFile={importJson} label="Импорт JSON" />
                <button type="button" className="signsButton is-primary" disabled={creating} onClick={create}>
                    {creating ? "Создаём…" : "+ Новый профиль"}
                </button>
            </div>

            <PageMessage message={message} onClose={() => setMessage(null)} />

            <div className="signsTable is-profiles">
                <div className="signsTable__row is-head">
                    <span>#</span>
                    <span>Профиль</span>
                    <span>Фирма</span>
                    <span>Контакты · услуги</span>
                    <span>Обновлено</span>
                    <span>Вывесок</span>
                    <span className="is-right">Действия</span>
                </div>
                {loading ? <div className="signsTable__empty"><Loader /></div> : null}
                {!loading && !filtered.length ? (
                    <div className="signsTable__empty">{profiles.length ? "Ничего не нашлось." : "Профилей пока нет. Их можно создать здесь или прямо в вывеске."}</div>
                ) : null}
                {filtered.map((profile, index) => {
                    const used = signs.filter((sign) => sign.profileId === profile._id).length;
                    return (
                        <div key={profile._id} className="signsTable__group">
                            <div className="signsTable__row">
                                <span className="signsTable__num">{index + 1}</span>
                                <button type="button" className="signsTable__name" onClick={() => navigate(`${PROFILES_ROOT}/${profile._id}`)}>
                                    <i className="printSigns__swatch is-profile" />
                                    <strong>{profile.name}</strong>
                                </button>
                                <span className="signsTable__muted">{profile.brandName || "—"}{profile.tagline ? ` · ${profile.tagline}` : ""}</span>
                                <span className="signsTable__muted">тел. {profile.phones.length} · услуг {profile.services.length}</span>
                                <span>{formatDateTime(profile.updatedAt).split(",")[0] || "—"}</span>
                                <span>{used ? <StatusBadge tone="count">{used}</StatusBadge> : <StatusBadge>нет</StatusBadge>}</span>
                                <span className="is-right">
                                    <ActionsMenu
                                        busy={busy[profile._id]}
                                        disabled={!!busy[profile._id]}
                                        items={[
                                            { label: "Открыть", onClick: () => navigate(`${PROFILES_ROOT}/${profile._id}`) },
                                            { label: "Экспорт JSON", onClick: () => run(profile, "Выгружаем…", async () => {
                                                const { blob, filename } = await exportProfileJson(profile._id);
                                                saveAs(blob, filename);
                                            }, "Не удалось выгрузить JSON") },
                                            { label: "Сделать копию", onClick: () => run(profile, "Копируем…", async () => {
                                                const copy = await duplicateProfile(profile._id);
                                                setProfiles((current) => [copy, ...current]);
                                            }, "Не удалось скопировать") },
                                            {
                                                label: "Удалить",
                                                danger: true,
                                                divider: true,
                                                onClick: () => {
                                                    if (!window.confirm(`Удалить профиль «${profile.name}»?`)) return;
                                                    run(profile, "Удаляем…", async () => {
                                                        await deleteProfile(profile._id);
                                                        setProfiles((current) => current.filter((item) => item._id !== profile._id));
                                                    }, "Не удалось удалить");
                                                },
                                            },
                                        ]}
                                    />
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </SignsPage>
    );
};

/** Один профиль: слева поля, справа вывеска с ним. */
export const PrintSignProfilePage = () => {
    const { profileId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [title, setTitle] = useState("Профиль бренда");

    useEffect(() => {
        let alive = true;
        Promise.all([getSignMeta(), listAssets(), listSigns(), listProfiles()])
            .then(([meta, assets, signs, profiles]) => {
                if (!alive) return;
                setData({ meta, assets, signs });
                setTitle(profiles.find((item) => item._id === profileId)?.name || "Профиль бренда");
            })
            .catch(async (loadError) => alive && setError(await readApiError(loadError, "Профиль не открылся")));
        return () => {
            alive = false;
        };
    }, [profileId]);

    return (
        <SignsPage title={title} subtitle="Из профиля берут данные все вывески с ним" back={PROFILES_ROOT} backLabel="К профилям" wide>
            {!data ? (
                <div className="signsEditor__placeholder">{error || <Loader />}</div>
            ) : (
                <div className="signsEditor is-profile">
                    <ProfileEditor
                        key={profileId}
                        profileId={profileId}
                        meta={data.meta}
                        assets={data.assets}
                        signs={data.signs}
                        onSaved={(profile) => setTitle(profile.name)}
                        onCreated={(profile) => navigate(`${PROFILES_ROOT}/${profile._id}`)}
                        onDeleted={() => navigate(PROFILES_ROOT)}
                    />
                </div>
            )}
        </SignsPage>
    );
};

export default PrintSignProfilesPage;
